import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { allTables, filledCells, hasDefaultTableName, readWorkbook } from "@/office/excel/workbook";
import { KEIN_INHALT } from "../schnittstelle";

export const lektionTabelleAnlegen: Lesson = {
  id: "tabelle-anlegen",
  title: "Aus einem Zellhaufen wird eine Tabelle",
  summary: "Strg + T macht aus einem losen Bereich ein Objekt, das seine eigenen Grenzen kennt.",
  durationMinutes: 12,
  learningGoals: [
    "Den Unterschied zwischen Zellbereich und Tabelle erklären",
    "Einen Datenbereich in eine Tabelle umwandeln",
    "Tabellen sprechend benennen",
  ],
  steps: [
    {
      id: "warum-tabelle",
      title: "Warum ein Bereich keine Tabelle ist",
      explanation: [
        "Bis hierher waren deine Daten ein Rechteck aus Zellen, das zufällig " +
          "zusammenhängt. Excel weiß nichts davon. Es sieht gefüllte Zellen und rät bei " +
          "jeder Gelegenheit neu, was zusammengehört: beim Sortieren, beim Filtern, beim " +
          "Erzeugen eines Diagramms. Meistens rät es richtig. Meistens.",
        "Eine Tabelle im Excel-Sinn ist etwas anderes: ein benanntes Objekt mit " +
          "eigenen Grenzen, einer echten Kopfzeile und der Fähigkeit, sich selbst zu " +
          "erweitern. Excel muss nicht mehr raten, es weiß. Diese Umwandlung ist die " +
          "Entsprechung zu den Formatvorlagen im Word-Kurs: Statt etwas so aussehen zu " +
          "lassen wie eine Tabelle, wird es eine.",
        "Was du dadurch bekommst, ist eine ganze Liste: Die Kopfzeile bleibt beim " +
          "Scrollen sichtbar, ohne dass etwas fixiert werden muss. Filterschaltflächen " +
          "sind sofort da. Neue Zeilen am unteren Rand werden automatisch Teil der " +
          "Tabelle, samt aller Formeln und Formate. Formeln in der Tabelle füllen sich " +
          "von selbst über die ganze Spalte aus. Und jede Auswertung, die auf die " +
          "Tabelle verweist – Diagramm, PivotTable, SUMME – wächst automatisch mit.",
        "Der letzte Punkt ist der wichtigste. Die häufigste Fehlerquelle in gewachsenen " +
          "Excel-Dateien ist eine Auswertung, die auf B2:B100 zeigt, während die Daten " +
          "längst bis Zeile 340 reichen. Das Ergebnis ist falsch und sieht richtig aus. " +
          "Mit einer Tabelle kann das nicht passieren.",
      ],
      keyPoints: [
        "Ein Zellbereich ist ein Rechteck, eine Tabelle ein Objekt mit Namen und Grenzen.",
        "Tabellen wachsen mit – Auswertungen darauf ebenfalls.",
        "Das ist der rote Faden dieses Moduls: Tabelle statt Zellhaufen.",
      ],
    },
    {
      id: "umwandeln",
      title: "Strg + T",
      explanation: [
        "Die Umwandlung selbst dauert zwei Sekunden: Cursor irgendwo in die Daten " +
          "setzen, Strg + T drücken. Excel schlägt den Bereich vor, den es erkannt hat, " +
          "und fragt, ob die Tabelle Überschriften enthält. Beides prüfen und " +
          "bestätigen – fertig.",
        "Bei der Frage nach den Überschriften lohnt der genaue Blick. Ist das Häkchen " +
          "gesetzt, wird deine erste Zeile zur Kopfzeile. Ist es nicht gesetzt, " +
          "erfindet Excel eine Kopfzeile mit „Spalte1“, „Spalte2“ – und deine " +
          "Überschriften rutschen in die Daten. Das ist ärgerlich, aber leicht zu " +
          "beheben, indem man die Umwandlung rückgängig macht und neu ansetzt.",
        "Der vorgeschlagene Bereich stimmt genau dann nicht, wenn in den Daten eine " +
          "Leerzeile oder Leerspalte steckt – Excel hört dort auf. Wenn der Vorschlag " +
          "zu klein ist, hast du gerade die Lücke gefunden, die auch beim Sortieren " +
          "Probleme gemacht hätte.",
        "Rückgängig geht das jederzeit: Cursor in die Tabelle, dann " +
          "„Tabellenentwurf“ → „In Bereich konvertieren“. Die Formatierung bleibt, die " +
          "Tabelleneigenschaften gehen verloren. Umgekehrt bleibt beim Umwandeln in " +
          "eine Tabelle jede vorhandene Formel erhalten.",
      ],
      keyPoints: [
        "Strg + T wandelt den erkannten Bereich in eine Tabelle um.",
        "Häkchen „Tabelle hat Überschriften“ prüfen, bevor du bestätigst.",
        "Zurück geht es über Tabellenentwurf → In Bereich konvertieren.",
      ],
      task: {
        instruction: "Wandle deine Datenliste mit Strg + T in eine Tabelle um.",
        hint: "Cursor irgendwo in die Daten setzen, Strg + T drücken, Bereich und Überschriften-Häkchen prüfen, bestätigen.",
        check: async () => {
          const mappe = await readWorkbook();
          const tabellen = allTables(mappe);

          if (tabellen.length === 0) {
            if (mappe.features.cells && filledCells(mappe).length === 0) {
              return fail(KEIN_INHALT, "Trage zuerst deine Liste ein – die Aufgabe aus Modul 1.");
            }
            return fail(
              "In der Arbeitsmappe gibt es noch keine Tabelle, nur Zellbereiche.",
              "Setze den Cursor in deine Daten und drücke Strg + T.",
            );
          }

          const ohneKopfzeile = tabellen.filter((tabelle) => tabelle.showHeaders === false);
          if (ohneKopfzeile.length > 0) {
            return fail(
              `Die Tabelle „${ohneKopfzeile[0].name}“ hat keine sichtbare Kopfzeile.`,
              "Setze den Cursor in die Tabelle und aktiviere unter „Tabellenentwurf“ das Häkchen " +
                "„Kopfzeile“. Ohne sie fehlt den Spalten der Name, über den strukturierte Verweise laufen.",
            );
          }

          const erste = tabellen[0];
          return pass(
            `Tabelle „${erste.name}“ auf Blatt „${erste.sheetName}“ über den Bereich ` +
              `${erste.address ?? "unbekannt"} – mit Kopfzeile und ${erste.columns.length} Spalten.`,
          );
        },
      },
    },
    {
      id: "benennen",
      title: "Der Tabellenname ist kein Detail",
      explanation: [
        "Excel tauft jede neue Tabelle „Tabelle1“, „Tabelle2“ und so weiter. Das ist " +
          "genauso wenig ein Name wie bei den Blättern, hat hier aber eine unmittelbare " +
          "Folge: Der Tabellenname taucht in jeder Formel auf, die auf die Tabelle " +
          "zeigt. =SUMME(Tabelle1[Spalte3]) sagt nichts. =SUMME(Umsatz[Betrag]) sagt " +
          "alles.",
        "Umbenannt wird die Tabelle im Feld links oben unter „Tabellenentwurf“ oder " +
          "über den Namens-Manager. Excel passt anschließend alle Formeln automatisch " +
          "an, die auf die Tabelle verweisen – wie bei den Blattnamen.",
        "Für Tabellennamen gelten dieselben Regeln wie für benannte Bereiche: keine " +
          "Leerzeichen, kein Anfangen mit einer Ziffer, und der Name darf nicht " +
          "aussehen wie ein Zellbezug. „Q1“ ist deshalb kein zulässiger Tabellenname, " +
          "„Umsatz_Q1“ dagegen schon. Ein Unterstrich hilft, wo man ein Leerzeichen " +
          "setzen würde.",
      ],
      keyPoints: [
        "Der Tabellenname steht in jeder Formel, die auf sie verweist.",
        "Umbenennen unter Tabellenentwurf, ganz links im Feld „Tabellenname“.",
        "Keine Leerzeichen, nicht mit einer Ziffer beginnen, nicht wie ein Zellbezug aussehen.",
      ],
      task: {
        instruction:
          "Gib deiner Tabelle einen sprechenden Namen – sie soll nicht mehr „Tabelle1“ " +
          "heißen.",
        hint: "Cursor in die Tabelle, dann Tabellenentwurf → ganz links das Feld „Tabellenname“.",
        check: async () => {
          const mappe = await readWorkbook();
          const tabellen = allTables(mappe);

          if (tabellen.length === 0) {
            return fail(
              "In der Arbeitsmappe gibt es noch keine Tabelle.",
              "Löse zuerst die Aufgabe im vorigen Schritt: Cursor in die Daten, Strg + T.",
            );
          }

          const standard = tabellen.filter(hasDefaultTableName);
          return fromCriteria(
            [
              criterion(
                `Mindestens eine Tabelle vorhanden (gefunden: ${tabellen.length})`,
                tabellen.length >= 1,
              ),
              criterion(
                standard.length === 0
                  ? "Alle Tabellen tragen einen selbst vergebenen Namen"
                  : `Noch mit Standardnamen: ${standard.map((tabelle) => `„${tabelle.name}“`).join(", ")}`,
                standard.length === 0,
              ),
            ],
            {
              success: `Benannt: ${tabellen.map((tabelle) => `„${tabelle.name}“`).join(", ")}. ` +
                "Damit lesen sich alle Formeln, die darauf verweisen, von selbst.",
              failure: "Da trägt noch eine Tabelle ihren Standardnamen.",
              hint: "Cursor in die Tabelle, dann Tabellenentwurf → Tabellenname. Ein Name wie „Umsatz“ oder " +
                "„Bestellungen“ macht jede spätere Formel lesbar.",
            },
          );
        },
      },
    },
  ],
};
